import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import HomePage from './pages/HomePage';
import BuyersPage from './pages/BuyersPage';
import SellersPage from './pages/SellersPage';
import ContactPage from './pages/ContactPage';
import Footer from './components/Footer';
import { useAnalytics } from './hooks/useAnalytics';

interface NavigationProps {
  currentPage: string;
  setCurrentPage: (page: string) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
}

const Navigation = ({ currentPage, setCurrentPage, isMenuOpen, setIsMenuOpen }: NavigationProps) => {
  const { trackEvent } = useAnalytics();

  const handlePageChange = (page: string) => {
    setCurrentPage(page);
    trackEvent('navigation_click', {
      page_name: page,
      navigation_type: 'main_menu'
    });
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
    trackEvent('menu_toggle', {
      action: isMenuOpen ? 'close' : 'open',
      device_type: 'mobile'
    });
  };

  return (
    <nav className="bg-white border-b">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <span className="text-xl font-bold text-blue-600">BottleCosts</span>
          </div>
          
          <div className="hidden sm:flex sm:items-center space-x-8">
            <button onClick={() => handlePageChange('home')} 
              className={`px-3 py-2 text-sm font-medium ${currentPage === 'home' ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'}`}>
              Home
            </button>
            <button onClick={() => handlePageChange('buyers')}
              className={`px-3 py-2 text-sm font-medium ${currentPage === 'buyers' ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'}`}>
              For Buyers
            </button>
            <button onClick={() => handlePageChange('sellers')}
              className={`px-3 py-2 text-sm font-medium ${currentPage === 'sellers' ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'}`}>
              For Sellers
            </button>
            <button onClick={() => handlePageChange('contact')}
              className={`px-3 py-2 text-sm font-medium ${currentPage === 'contact' ? 'text-blue-600' : 'text-gray-600 hover:text-blue-600'}`}>
              Contact
            </button>
            <a 
              href="https://app.bottlecosts.com/login" 
              className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-blue-600"
              onClick={() => trackEvent('auth_click', {
                action: 'sign_in',
                location: 'header'
              })}
            >
              Sign In
            </a>
            <a 
              href="https://app.bottlecosts.com/register" 
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              onClick={() => trackEvent('auth_click', {
                action: 'sign_up',
                location: 'header'
              })}
            >
              Sign Up
            </a>
          </div>

          <div className="flex items-center sm:hidden">
            <button onClick={handleMenuToggle} className="text-gray-600">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="sm:hidden p-2">
          <button onClick={() => { handlePageChange('home'); setIsMenuOpen(false); }}
            className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-blue-600 w-full text-left">
            Home
          </button>
          <button onClick={() => { handlePageChange('buyers'); setIsMenuOpen(false); }}
            className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-blue-600 w-full text-left">
            For Buyers
          </button>
          <button onClick={() => { handlePageChange('sellers'); setIsMenuOpen(false); }}
            className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-blue-600 w-full text-left">
            For Sellers
          </button>
          <button onClick={() => { handlePageChange('contact'); setIsMenuOpen(false); }}
            className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-blue-600 w-full text-left">
            Contact
          </button>
          <a 
            href="https://app.bottlecosts.com/login" 
            className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-blue-600 w-full text-left"
            onClick={() => trackEvent('auth_click', {
              action: 'sign_in',
              location: 'mobile_menu'
            })}
          >
            Sign In
          </a>
          <a 
            href="https://app.bottlecosts.com/register" 
            className="block px-3 py-2 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 w-full text-left"
            onClick={() => trackEvent('auth_click', {
              action: 'sign_up',
              location: 'mobile_menu'
            })}
          >
            Sign Up
          </a>
        </div>
      )}
    </nav>
  );
};

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { trackEvent, trackPageView } = useAnalytics();

  // Track page changes
  useEffect(() => {
    trackPageView(currentPage);
    trackEvent('page_view', {
      page_name: currentPage,
      referrer: document.referrer
    });
  }, [currentPage, trackEvent, trackPageView]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />
      <main className="flex-grow">
        {currentPage === 'home' && <HomePage setCurrentPage={setCurrentPage} />}
        {currentPage === 'buyers' && <BuyersPage setCurrentPage={setCurrentPage} />}
        {currentPage === 'sellers' && <SellersPage setCurrentPage={setCurrentPage} />}
        {currentPage === 'contact' && <ContactPage />}
      </main>
      <Footer setCurrentPage={setCurrentPage} />
    </div>
  );
};

export default App;